import type { ReactElement } from 'react';
import { createRootRoute, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router';
import type { PermissionCode } from '@amai/contracts';
import { AuthenticatedShell } from '@/layouts/AuthenticatedShell';
import { Guarded } from '@/layouts/Guarded';
import { LoginPage } from '@/routes/Login';
import { OverviewPage } from '@/routes/Overview';
import { EmpresaPage } from '@/routes/configuracoes/Empresa';
import { UsuariosPage } from '@/routes/configuracoes/Usuarios';
import { PerfisPage } from '@/routes/configuracoes/Perfis';
import { ApiKeysPage } from '@/routes/configuracoes/ApiKeys';
import { CertificadoPage } from '@/routes/configuracoes/Certificado';
import { AuditoriaPage } from '@/routes/configuracoes/Auditoria';
import { PessoasPage } from '@/routes/cadastros/Pessoas';
import { LancamentosPage } from '@/routes/financeiro/ContasPagar';
import { FornecedoresPage } from '@/routes/financeiro/Fornecedores';
import { AgendaPage, CustoPorCentroPage, DrePage, FluxoCaixaPage } from '@/routes/financeiro/Relatorios';
import { ContasPage } from '@/routes/financeiro/Contas';
import { CentrosCustoPage, PlanoContasPage } from '@/routes/financeiro/Arvores';
import { RequisicoesPage } from '@/routes/compras/Requisicoes';
import { CotacoesPage } from '@/routes/compras/Cotacoes';
import { PedidosPage } from '@/routes/compras/Pedidos';
import { PedidoImpressaoPage } from '@/routes/compras/PedidoImpressao';
import { CanalPage } from '@/routes/agentes/Canal';
import { MensagensPage } from '@/routes/agentes/Mensagens';
import { RecebimentosPage } from '@/routes/compras/Recebimentos';
import { RecebimentoConferenciaPage } from '@/routes/compras/RecebimentoConferencia';
import { DocumentosPage } from '@/routes/compras/Documentos';
import { DocumentoDetalhePage } from '@/routes/compras/DocumentoDetalhe';
import { ItensPage } from '@/routes/estoque/Itens';
import { CobrancaPage } from '@/routes/configuracoes/Cobranca';
import { CobrancasPage } from '@/routes/financeiro/Cobrancas';
import { SaldosPage } from '@/routes/estoque/Saldos';
import { ValidadesPage } from '@/routes/estoque/Validades';
import { MovimentacoesPage } from '@/routes/estoque/Movimentacoes';
import { ConsumoPage } from '@/routes/estoque/Consumo';
import { InventarioPage } from '@/routes/estoque/Inventario';
import { LocaisPage } from '@/routes/estoque/Locais';
import { PlanosPage } from '@/routes/clube/Planos';
import { AssociadosPage } from '@/routes/clube/Associados';
import { RestritosPage } from '@/routes/clube/Restritos';
import { PainelVendasPage } from '@/routes/clube/PainelVendas';
import { MensalidadesPage } from '@/routes/clube/Mensalidades';
import { InadimplenciaPage } from '@/routes/clube/Inadimplencia';
import { ColaboradoresPage } from '@/routes/rh/Colaboradores';
import { AfastamentosPage } from '@/routes/rh/Afastamentos';
import { FolhaPage } from '@/routes/rh/Folha';
import { CargosPage } from '@/routes/rh/Cargos';
import { hasSession } from '@/lib/session';
import { NotFoundPage } from '@/routes/Fallbacks';

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: () => {
    // Quem já tem sessão não precisa ver a tela de login.
    if (hasSession()) throw redirect({ to: '/' });
  },
});

/**
 * Impressão do pedido de compra.
 *
 * Mora FORA do shell de propósito: o que vai para a impressora é o documento,
 * sem menu lateral nem cabeçalho. A permissão de ver pedido é cobrada pela
 * API na hora de carregar — aqui só se exige a sessão.
 */
const pedidoImpressaoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/compras/pedidos/$id/imprimir',
  beforeLoad: ({ location }) => {
    if (!hasSession()) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: function PedidoImpressaoRoute() {
    const { id } = pedidoImpressaoRoute.useParams();
    return <PedidoImpressaoPage id={id} />;
  },
});

/**
 * Tudo abaixo daqui exige sessão. A checagem acontece em dois tempos: o
 * `beforeLoad` corta a navegação sem token, e o componente espera o `/auth/me`
 * confirmar antes de desenhar o shell (evita piscar tela protegida).
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ location }) => {
    if (!hasSession()) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: AuthenticatedShell,
});

const overviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: OverviewPage,
});

/**
 * Toda rota de módulo nasce com a permissão que a protege ao lado do caminho —
 * a mesma que a sidebar usa para decidir se mostra o item. O servidor checa de
 * novo; isto evita levar o operador a uma tela que ele não pode abrir.
 */
function guardedRoute(
  path: string,
  permission: PermissionCode | PermissionCode[],
  Page: () => ReactElement,
) {
  return createRoute({
    getParentRoute: () => appRoute,
    path,
    component: () => (
      <Guarded permission={permission}>
        <Page />
      </Guarded>
    ),
  });
}

/**
 * Conferência do recebimento: página própria, dentro do shell. Um pedido de
 * trinta linhas não cabe num drawer, e em tablet nem se tenta.
 */
const recebimentoConferenciaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/compras/recebimentos/$id/conferir',
  component: function RecebimentoConferenciaRoute() {
    const { id } = recebimentoConferenciaRoute.useParams();
    return (
      <Guarded permission="recebimentos:ver">
        <RecebimentoConferenciaPage id={id} />
      </Guarded>
    );
  },
});

/**
 * Detalhe do documento fiscal: página própria, dentro do shell. Conferência,
 * vínculos, divergências e integração não cabem num drawer.
 */
const documentoDetalheRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/compras/documentos/$id',
  component: function DocumentoDetalheRoute() {
    const { id } = documentoDetalheRoute.useParams();
    return (
      <Guarded permission="documentos-fiscais:ver">
        <DocumentoDetalhePage id={id} />
      </Guarded>
    );
  },
});

const moduleRoutes = [
  recebimentoConferenciaRoute,
  documentoDetalheRoute,
  // Cadastros
  guardedRoute('/cadastros/pessoas', 'pessoas:ver', PessoasPage),

  // Financeiro
  guardedRoute('/financeiro/contas-pagar', 'lancamentos:ver', () => (
    <LancamentosPage type="PAYABLE" />
  )),
  guardedRoute('/financeiro/cobrancas', 'cobrancas:ver', CobrancasPage),
  guardedRoute('/financeiro/contas-receber', 'lancamentos:ver', () => (
    <LancamentosPage type="RECEIVABLE" />
  )),
  guardedRoute('/financeiro/fornecedores', ['pessoas:ver', 'lancamentos:ver'], FornecedoresPage),
  guardedRoute('/financeiro/agenda', 'lancamentos:ver', AgendaPage),
  guardedRoute('/financeiro/fluxo-caixa', 'lancamentos:ver', FluxoCaixaPage),
  guardedRoute('/financeiro/custo-por-centro', 'lancamentos:ver', CustoPorCentroPage),
  guardedRoute('/financeiro/dre', 'lancamentos:ver', DrePage),
  guardedRoute('/financeiro/contas', 'contas-bancarias:ver', ContasPage),
  guardedRoute('/financeiro/centros-custo', 'centros-custo:ver', CentrosCustoPage),
  guardedRoute('/financeiro/plano-contas', 'plano-contas:ver', PlanoContasPage),

  // Compras
  guardedRoute('/compras/requisicoes', 'requisicoes:ver', RequisicoesPage),
  guardedRoute('/compras/cotacoes', 'cotacoes:ver', CotacoesPage),
  guardedRoute('/compras/pedidos', 'pedidos-compra:ver', PedidosPage),
  guardedRoute('/compras/recebimentos', 'recebimentos:ver', RecebimentosPage),
  guardedRoute('/compras/documentos', 'documentos-fiscais:ver', DocumentosPage),

  // Estoque
  guardedRoute('/estoque/itens', 'itens:ver', ItensPage),
  guardedRoute('/estoque/saldos', 'movimentacoes:ver', SaldosPage),
  guardedRoute('/estoque/validades', 'movimentacoes:ver', ValidadesPage),
  guardedRoute('/estoque/movimentacoes', 'movimentacoes:ver', MovimentacoesPage),
  guardedRoute('/estoque/inventario', 'inventarios:ver', InventarioPage),
  guardedRoute('/estoque/consumo', 'movimentacoes:ver', ConsumoPage),
  guardedRoute('/estoque/locais', 'locais-estoque:ver', LocaisPage),

  // Clube AMAI Pass
  guardedRoute('/clube/planos', 'planos-socios:ver', PlanosPage),
  guardedRoute('/clube/associados', 'associados:ver', AssociadosPage),
  guardedRoute('/clube/painel-vendas', 'associados:ver', PainelVendasPage),
  guardedRoute('/clube/mensalidades', 'mensalidades:ver', MensalidadesPage),
  guardedRoute('/clube/inadimplencia', 'inadimplencia:ver', InadimplenciaPage),
  guardedRoute('/clube/restritos', 'restritos:ver', RestritosPage),

  // RH
  guardedRoute('/rh/colaboradores', 'colaboradores:ver', ColaboradoresPage),
  guardedRoute('/rh/afastamentos', 'afastamentos:ver', AfastamentosPage),
  guardedRoute('/rh/folha', 'folha-pagamento:ver', FolhaPage),
  guardedRoute('/rh/cargos', 'departamentos:ver', CargosPage),

  // Agentes
  guardedRoute('/agentes/canal', 'canal-mensagens:ver', CanalPage),
  guardedRoute('/agentes/mensagens', 'mensagens:ver', MensagensPage),

  // Configurações
  guardedRoute('/configuracoes', 'empresas:ver', EmpresaPage),
  guardedRoute('/configuracoes/usuarios', 'usuarios:ver', UsuariosPage),
  guardedRoute('/configuracoes/perfis', 'perfis:ver', PerfisPage),
  guardedRoute('/configuracoes/api-keys', 'api-keys:ver', ApiKeysPage),
  guardedRoute('/configuracoes/certificado', 'certificado-digital:ver', CertificadoPage),
  // Duas permissões: a tela escolhe a conta que recebe as baixas, e essa
  // lista é de `contas-bancarias`. Só com `gateway-pagamento` ela abriria e
  // quebraria na primeira chamada, com o seletor vazio e sem explicação.
  guardedRoute('/configuracoes/cobranca', ['gateway-pagamento:ver', 'contas-bancarias:ver'], CobrancaPage),
  guardedRoute('/configuracoes/auditoria', 'auditoria:ver', AuditoriaPage),
];

const routeTree = rootRoute.addChildren([
  loginRoute,
  pedidoImpressaoRoute,
  appRoute.addChildren([overviewRoute, ...moduleRoutes]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
